import { ActivityScheduleRule } from '@faro/study-designer-model';

/***
 * Compare arrayy item by item.
 * @param a The first array to compare
 * @param b The second array to compare
 */
export function compareArrays<T>(a: T[] | undefined, b: T[] | undefined): boolean {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }
    if (a.length !== b.length) {
        return false;
    }
    const aSorted = a.toSorted();
    const bSorted = b.toSorted();
    for (let i = 0; i < aSorted.length; i++) {
        if (aSorted[i] !== bSorted[i]) {
            return false;
        }
    }
    return true;
}

function isObjectFieldEqual<T extends object>(a: T, b: T, fields: (keyof T)[]) {
    return fields.every(field => {
        if (Array.isArray(a[field]) && Array.isArray(b[field])) {
            return compareArrays(a[field], b[field]);
        }
        return a[field] === b[field];
    });
}

/***
 * Compare two {@link ActivityScheduleRule} objects field by field, based on the rule type.
 * It does not compare the `id` field.
 * @param a The first {@link ActivityScheduleRule} to compare
 * @param b The second {@link ActivityScheduleRule} to compare
 */
export function isEqualActivityScheduleRule(a: ActivityScheduleRule, b: ActivityScheduleRule): boolean {
    // generic fields
    if (!isObjectFieldEqual(a, b, ['type', 'activityId', 'epochId', 'scheduleOfActivitiesId'])) {
        return false;
    }

    if (a.type === 'unscheduled-visit' || a.type === 'early-termination') {
        return isObjectFieldEqual(a, b, ['cardinality', 'description']);
    }

    if (a.type === 'fixed') {
        return isObjectFieldEqual(a, b, ['epochDay', 'cycles', 'cardinality', 'description']);
    }

    if (a.type === 'custom-reported' || a.type === 'custom-non-reported') {
        return isObjectFieldEqual(a, b, ['epochDay', 'endEpochDay', 'cycles', 'cardinality', 'description']);
    }

    if (a.type === 'non-fixed-day') {
        return isObjectFieldEqual(a, b, ['epochDay', 'endEpochDay', 'cycles', 'cardinality', 'description']);
    }

    if (a.type === 'recurring') {
        return isObjectFieldEqual(a, b, [
            'epochDay',
            'endEpochDay',
            'epochId',
            'endEpochId',
            'startCycle',
            'endCycle',
            'cardinality',
            'description',
        ]);
    }
    return false;
}
